import axios from "axios";

export interface ApiErrorPayload {
  code: string;
  message: string;
  details: unknown;
  timestamp?: string;
}

export interface ApiErrorResponseBody {
  success: false;
  error: ApiErrorPayload;
}

export class ApiError extends Error {
  statusCode: number | null;
  code: string | null;
  originalError: unknown;

  constructor(
    message: string,
    statusCode: number | null = null,
    originalError: unknown = null,
    code: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.statusCode = statusCode;
    this.code = code;
    this.originalError = originalError;
  }
}

function isApiErrorPayload(value: unknown): value is ApiErrorPayload {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as ApiErrorPayload).message === "string"
  );
}

export function extractApiErrorPayload(
  error: unknown,
): ApiErrorPayload | null {
  if (!axios.isAxiosError(error)) return null;

  const data = error.response?.data as Record<string, unknown> | undefined;
  if (!data) return null;

  if (isApiErrorPayload(data.error)) {
    return data.error;
  }

  return null;
}

export function extractApiErrorMessage(error: unknown): string {
  const apiError = extractApiErrorPayload(error);
  if (apiError?.message) {
    return apiError.message;
  }

  if (axios.isAxiosError(error)) {
    const data = error.response?.data as Record<string, unknown> | undefined;
    const statusText = error.response?.statusText;

    if (data) {
      if (typeof data.detail === "string" && data.detail) return data.detail;
      if (typeof data.message === "string" && data.message) return data.message;
      if (typeof data.error === "string" && data.error) return data.error;
      if (typeof data.title === "string" && data.title) return data.title;

      if (Array.isArray(data.errors)) {
        const messages = data.errors
          .map((e) =>
            typeof e === "string" ? e : (e as { message?: string }).message,
          )
          .filter(Boolean);
        if (messages.length > 0) return messages.join(", ");
      }
    }

    if (statusText) return statusText;
  }

  if (error instanceof Error) return error.message;
  return "Something went wrong. Please try again.";
}

export function extractApiErrorCode(error: unknown): string | null {
  const apiError = extractApiErrorPayload(error);
  if (apiError?.code) return apiError.code;

  return null;
}

export function getErrorStatusCode(error: unknown): number | null {
  if (axios.isAxiosError(error)) {
    return error.response?.status ?? null;
  }
  if (error instanceof ApiError) {
    return error.statusCode;
  }
  return null;
}
